<script setup lang="ts">
import { computed } from 'vue'
import type { VpnKey } from '@/types'

const props = defineProps<{ keys: VpnKey[] }>()

// Уникальный id сервера ключа (для отображения), транзитные ключи не считаем серверами.
function serverIdOf(k: VpnKey): number | null {
  if (k.is_transit_key) return null
  const id = k.display_server_id ?? k.server_id
  return id && id > 0 ? id : null
}

// Свободные (не подписочные) серверы пользователя.
const freeStats = computed(() => {
  const all = new Set<number>()
  const active = new Set<number>()
  for (const k of props.keys) {
    if (k.is_subscription_key) continue
    const id = serverIdOf(k)
    if (id === null) continue
    all.add(id)
    if (!k.is_expired) active.add(id)
  }
  return { active: active.size, total: all.size }
})

// Подписочные серверы пользователя (активные = неистёкшие).
const subStats = computed(() => {
  const active = new Set<number>()
  for (const k of props.keys) {
    if (!k.is_subscription_key || k.is_expired) continue
    const id = serverIdOf(k)
    if (id === null) continue
    active.add(id)
  }
  return { active: active.size }
})

const hasFree = computed(() => freeStats.value.total > 0)
const hasSub = computed(() => subStats.value.active > 0)
const visible = computed(() => hasFree.value || hasSub.value)
</script>

<template>
  <div
    v-if="visible"
    class="panel flex flex-wrap items-center gap-x-4 gap-y-1 !py-3 !px-4"
  >
    <div class="flex items-center gap-2">
      <span class="badge badge-outline-primary">🖧</span>
      <span class="text-sm font-bold">Активные серверы</span>
    </div>

    <!-- Только свободные: активные/всего -->
    <div v-if="hasFree && !hasSub" class="text-sm">
      <span class="text-lg font-extrabold text-primary">{{ freeStats.active }}</span>
      <span class="text-muted">/{{ freeStats.total }}</span>
    </div>

    <!-- Свободные + подписка: раздельно -->
    <div v-else-if="hasFree && hasSub" class="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
      <span>
        По подписке:
        <span class="font-extrabold text-primary">{{ subStats.active }}</span>
      </span>
      <span>
        Вне подписки:
        <span class="font-extrabold text-primary">{{ freeStats.active }}</span>
      </span>
    </div>

    <!-- Только подписка: одно число -->
    <div v-else class="text-sm">
      <span class="text-lg font-extrabold text-primary">{{ subStats.active }}</span>
    </div>
  </div>
</template>
